import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/api/client";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "";
  const [query, setQuery] = useState(q);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])); }, []);

  useEffect(() => {
    setLoading(true);
    const p = { q, limit: 200 };
    if (category) p.category = category;
    if (sort) p.sort = sort;
    api.get("/search", { params: p })
      .then((r) => setProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [q, category, sort]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const topLevel = cats.filter((c) => !c.parent_slug);

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-10" data-testid="search-results-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Search</div>
        <h1 className="font-display text-3xl md:text-4xl">{q ? <>Results for "{q}"</> : "Search"}</h1>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); setParam("q", query.trim()); }}
        className="flex gap-2 mb-5"
      >
        <Input
          data-testid="search-page-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products, categories, brands…"
          className="h-11"
        />
        <Button data-testid="search-page-submit" type="submit" className="h-11 rounded-full bg-[#F48E2E] hover:bg-[#D97815]">Search</Button>
      </form>

      <div className="flex flex-wrap gap-3 items-center mb-6">
        <label className="text-xs text-gray-500">Category</label>
        <select
          data-testid="search-filter-category"
          value={category}
          onChange={(e) => setParam("category", e.target.value)}
          className="h-9 border border-gray-300 rounded-md px-2 text-sm bg-white"
        >
          <option value="">All categories</option>
          {topLevel.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <label className="text-xs text-gray-500 ml-4">Sort</label>
        <select
          data-testid="search-filter-sort"
          value={sort}
          onChange={(e) => setParam("sort", e.target.value)}
          className="h-9 border border-gray-300 rounded-md px-2 text-sm bg-white"
        >
          <option value="">Relevance / newest</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
        </select>
        <span className="ml-auto text-xs text-gray-500" data-testid="search-count">{loading ? "Searching…" : `${products.length} product(s)`}</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-gray-500" data-testid="search-empty">No products found for your search.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}

      <div className="mt-8">
        <Link to="/" className="text-sm text-[#F48E2E] hover:underline">← Back to home</Link>
      </div>
    </div>
  );
}
